import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Form,
  Input,
  Select,
  Switch,
  Modal,
  message,
  Space,
  Popconfirm,
  Tag,
  Tooltip,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

const { Option } = Select;

interface ChannelConfigData {
  id: number;
  channelName: string; // 渠道名称
  promotionDomain: string; // 推广域名
  promotionType: 'landing_page' | 'h5' | 'other'; // 推广类型
  channelPackage: string; // 渠道包选择
  channelUtm: string; // 渠道UTM（自动生成，不显示）
  adjustEnabled: boolean; // adjust归因开关
  adjustAttributionUrl?: string; // adjust归因地址
  facebookEnabled: boolean; // facebook归因开关
  facebookPixelId?: string; // Facebook 像素Id
  facebookPixelToken?: string; // Facebook 像素token
  tiktokEnabled: boolean; // tiktok归因开关
  tiktokPixelId?: string; // tiktok 像素Id
  tiktokPixelToken?: string; // tiktok 像素token
  creator: string;
  createTime: string;
  updater: string;
  updateTime: string;
}

const ChannelConfig: React.FC = () => {
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState<ChannelConfigData[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ChannelConfigData | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // TODO: 替换为实际API调用
      // const response = await api.get('/channel-configs');
      // setDataSource(response.data);
      
      // 模拟数据
      const mockData: ChannelConfigData[] = [
        {
          id: 0, // 默认渠道ID为0，表示系统默认渠道
          channelName: '默认渠道',
          promotionDomain: '**.**',
          promotionType: 'other',
          channelPackage: 'aalive游戏包',
          channelUtm: 'utm_source=default&utm_medium=system',
          adjustEnabled: false,
          facebookEnabled: false,
          tiktokEnabled: false,
          creator: 'system',
          createTime: '2025-01-01 00:00:00',
          updater: 'system',
          updateTime: '2025-01-01 00:00:00',
        },
        {
          id: 1,
          channelName: 'Facebook推广',
          promotionDomain: 'https://aa4.live',
          promotionType: 'landing_page',
          channelPackage: 'aalive游戏包',
          channelUtm: 'utm_source=facebook&utm_medium=cpc',
          adjustEnabled: true,
          adjustAttributionUrl: 'https://app.adjust.com/xxx',
          facebookEnabled: true,
          facebookPixelId: '123456789',
          facebookPixelToken: 'token_abc123',
          tiktokEnabled: false,
          creator: 'admin',
          createTime: '2025-01-09 10:00:00',
          updater: 'admin',
          updateTime: '2025-01-09 10:00:00',
        },
        {
          id: 2,
          channelName: 'TikTok推广',
          promotionDomain: 'https://aa5.live',
          promotionType: 'h5',
          channelPackage: 'aalive游戏包',
          channelUtm: 'utm_source=tiktok&utm_medium=cpc',
          adjustEnabled: false,
          facebookEnabled: false,
          tiktokEnabled: true,
          tiktokPixelId: '987654321',
          tiktokPixelToken: 'token_xyz789',
          creator: 'admin',
          createTime: '2025-01-09 11:00:00',
          updater: 'admin',
          updateTime: '2025-01-09 11:00:00',
        },
      ];
      setDataSource(mockData);
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      adjustEnabled: false,
      facebookEnabled: false,
      tiktokEnabled: false,
      promotionType: 'landing_page',
      channelPackage: 'aalive游戏包',
    });
    setModalVisible(true);
  };

  const handleEdit = (record: ChannelConfigData) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    // 默认渠道不允许删除
    if (id === 0) {
      message.warning('默认渠道不允许删除');
      return;
    }
    
    try {
      // TODO: 替换为实际API调用
      // await api.delete(`/channel-configs/${id}`);
      
      setDataSource(dataSource.filter(item => item.id !== id));
      message.success('删除成功');
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    // 默认渠道不允许修改
    if (editingRecord && editingRecord.id === 0) {
      message.warning('默认渠道不允许修改');
      return;
    }
    
    try {
      // 验证域名格式（默认渠道除外）
      if (values.promotionDomain !== '**.**' && 
          !values.promotionDomain.startsWith('http://') && 
          !values.promotionDomain.startsWith('https://')) {
        message.error('推广域名必须以 http:// 或 https:// 开头');
        return;
      }

      // 检查域名唯一性（编辑时排除当前记录）
      const existingRecord = dataSource.find(
        item => item.promotionDomain === values.promotionDomain && item.id !== editingRecord?.id
      );
      if (existingRecord) {
        message.error('该推广域名已存在，不能重复配置');
        return;
      }

      if (editingRecord) {
        // 更新
        // TODO: 替换为实际API调用
        // await api.put(`/channel-configs/${editingRecord.id}`, values);
        
        setDataSource(dataSource.map(item => 
          item.id === editingRecord.id 
            ? { ...item, ...values, updateTime: new Date().toLocaleString('zh-CN') }
            : item
        ));
        message.success('更新成功');
      } else {
        // 新增
        // TODO: 替换为实际API调用
        // const response = await api.post('/channel-configs', values);
        
        const newRecord: ChannelConfigData = {
          id: Date.now(),
          ...values,
          channelUtm: `utm_source=${values.channelName}&utm_medium=promotion`, // 自动生成UTM
          creator: 'admin', // TODO: 从用户信息获取
          createTime: new Date().toLocaleString('zh-CN'),
          updater: 'admin',
          updateTime: new Date().toLocaleString('zh-CN'),
        };
        setDataSource([...dataSource, newRecord]);
        message.success('新增成功');
      }
      
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };

  const getPromotionTypeName = (type: string) => {
    const map: Record<string, string> = {
      landing_page: '落地页',
      h5: 'H5',
      other: '其他',
    };
    return map[type] || type;
  };

  const columns = [
    {
      title: '渠道名称',
      dataIndex: 'channelName',
      key: 'channelName',
      width: 180,
      render: (text: string, record: ChannelConfigData) => (
        <Space>
          {text}
          {record.id === 0 && (
            <Tag color="orange">系统默认</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '推广域名',
      dataIndex: 'promotionDomain',
      key: 'promotionDomain',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: '推广类型',
      dataIndex: 'promotionType',
      key: 'promotionType',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'landing_page' ? 'blue' : type === 'h5' ? 'green' : 'default'}>
          {getPromotionTypeName(type)}
        </Tag>
      ),
    },
    {
      title: '渠道包选择',
      dataIndex: 'channelPackage',
      key: 'channelPackage',
      width: 150,
    },
    {
      title: 'Adjust归因',
      key: 'adjust',
      width: 120,
      render: (_: any, record: ChannelConfigData) => (
        <Space>
          <Tag color={record.adjustEnabled ? 'green' : 'default'}>
            {record.adjustEnabled ? '开启' : '关闭'}
          </Tag>
          {record.adjustEnabled && record.adjustAttributionUrl && (
            <Tooltip title={record.adjustAttributionUrl}>
              <Tag color="blue">已配置</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Facebook归因',
      key: 'facebook',
      width: 120,
      render: (_: any, record: ChannelConfigData) => (
        <Space>
          <Tag color={record.facebookEnabled ? 'green' : 'default'}>
            {record.facebookEnabled ? '开启' : '关闭'}
          </Tag>
          {record.facebookEnabled && record.facebookPixelId && (
            <Tooltip title={`像素ID: ${record.facebookPixelId}`}>
              <Tag color="blue">已配置</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'TikTok归因',
      key: 'tiktok',
      width: 120,
      render: (_: any, record: ChannelConfigData) => (
        <Space>
          <Tag color={record.tiktokEnabled ? 'green' : 'default'}>
            {record.tiktokEnabled ? '开启' : '关闭'}
          </Tag>
          {record.tiktokEnabled && record.tiktokPixelId && (
            <Tooltip title={`像素ID: ${record.tiktokPixelId}`}>
              <Tag color="blue">已配置</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: ChannelConfigData) => {
        const isDefaultChannel = record.id === 0;
        return (
          <Space>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              编辑
            </Button>
            {!isDefaultChannel && (
              <Popconfirm
                title="确定要删除这条记录吗？"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                >
                  删除
                </Button>
              </Popconfirm>
            )}
            {isDefaultChannel && (
              <Tooltip title="默认渠道不允许删除">
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  disabled
                >
                  删除
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title="渠道配置"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadData}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增渠道
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1200 }}
          pagination={{
            total: dataSource.length,
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
        />
      </Card>

      <Modal
        title={editingRecord ? (editingRecord.id === 0 ? '查看默认渠道配置（不可修改）' : '编辑渠道配置') : '新增渠道配置'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={800}
        destroyOnClose
        footer={editingRecord && editingRecord.id === 0 ? [
          <Button key="close" onClick={() => {
            setModalVisible(false);
            form.resetFields();
          }}>
            关闭
          </Button>
        ] : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {editingRecord && editingRecord.id === 0 && (
            <Alert
              message="默认渠道说明"
              description="默认渠道用于归属未配置渠道的域名或来源进入系统的用户。此渠道不可修改和删除。"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}
          <Form.Item
            label="渠道名称"
            name="channelName"
            rules={[{ required: true, message: '请输入渠道名称' }]}
          >
            <Input 
              placeholder="请输入渠道名称" 
              disabled={editingRecord?.id === 0}
            />
          </Form.Item>

          <Form.Item
            label="推广域名"
            name="promotionDomain"
            rules={[
              { required: true, message: '请输入推广域名' },
              {
                validator: (_: any, value: string) => {
                  if (editingRecord?.id === 0) {
                    // 默认渠道必须是 **.**
                    if (value === '**.**') {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('默认渠道域名固定为 **.**'));
                  } else {
                    // 其他渠道必须是 http:// 或 https:// 开头
                    if (value && (value.startsWith('http://') || value.startsWith('https://'))) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('域名必须以 http:// 或 https:// 开头，例如：https://aa4.live'));
                  }
                },
              },
            ]}
            extra={editingRecord?.id === 0 
              ? "默认渠道域名固定为 **.**，用于匹配未配置渠道的用户" 
              : "必须完整配置，例如：https://aa4.live（域名唯一，不支持重复配置）"}
          >
            <Input 
              placeholder={editingRecord?.id === 0 ? "**.**" : "https://aa4.live"} 
              disabled={editingRecord?.id === 0}
            />
          </Form.Item>

          <Form.Item
            label="推广类型"
            name="promotionType"
            rules={[{ required: true, message: '请选择推广类型' }]}
          >
            <Select 
              placeholder="请选择推广类型"
              disabled={editingRecord?.id === 0}
            >
              <Option value="landing_page">落地页</Option>
              <Option value="h5">H5</Option>
              <Option value="other">其他</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="渠道包选择"
            name="channelPackage"
            rules={[{ required: true, message: '请选择渠道包' }]}
          >
            <Select 
              placeholder="请选择渠道包"
              disabled={editingRecord?.id === 0}
            >
              <Option value="aalive游戏包">aalive游戏包</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Adjust归因配置">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item
                name="adjustEnabled"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Switch 
                  checkedChildren="开启" 
                  unCheckedChildren="关闭" 
                  disabled={editingRecord?.id === 0}
                />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.adjustEnabled !== currentValues.adjustEnabled
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('adjustEnabled') ? (
                    <Form.Item
                      name="adjustAttributionUrl"
                      label="Adjust归因地址"
                      rules={[{ required: true, message: '请输入Adjust归因地址' }]}
                    >
                      <Input 
                        placeholder="https://app.adjust.com/xxx" 
                        disabled={editingRecord?.id === 0}
                      />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item label="Facebook归因配置">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item
                name="facebookEnabled"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Switch 
                  checkedChildren="开启" 
                  unCheckedChildren="关闭" 
                  disabled={editingRecord?.id === 0}
                />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.facebookEnabled !== currentValues.facebookEnabled
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('facebookEnabled') ? (
                    <>
                      <Form.Item
                        name="facebookPixelId"
                        label="Facebook 像素Id"
                        rules={[{ required: true, message: '请输入Facebook像素Id' }]}
                      >
                        <Input 
                          placeholder="请输入Facebook像素Id" 
                          disabled={editingRecord?.id === 0}
                        />
                      </Form.Item>
                      <Form.Item
                        name="facebookPixelToken"
                        label="Facebook 像素token"
                        rules={[{ required: true, message: '请输入Facebook像素token' }]}
                      >
                        <Input 
                          placeholder="请输入Facebook像素token" 
                          disabled={editingRecord?.id === 0}
                        />
                      </Form.Item>
                    </>
                  ) : null
                }
              </Form.Item>
            </Space>
          </Form.Item>

          <Form.Item label="TikTok归因配置">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Form.Item
                name="tiktokEnabled"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Switch 
                  checkedChildren="开启" 
                  unCheckedChildren="关闭" 
                  disabled={editingRecord?.id === 0}
                />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.tiktokEnabled !== currentValues.tiktokEnabled
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('tiktokEnabled') ? (
                    <>
                      <Form.Item
                        name="tiktokPixelId"
                        label="TikTok 像素Id"
                        rules={[{ required: true, message: '请输入TikTok像素Id' }]}
                      >
                        <Input 
                          placeholder="请输入TikTok像素Id" 
                          disabled={editingRecord?.id === 0}
                        />
                      </Form.Item>
                      <Form.Item
                        name="tiktokPixelToken"
                        label="TikTok 像素token"
                        rules={[{ required: true, message: '请输入TikTok像素token' }]}
                      >
                        <Input 
                          placeholder="请输入TikTok像素token" 
                          disabled={editingRecord?.id === 0}
                        />
                      </Form.Item>
                    </>
                  ) : null
                }
              </Form.Item>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ChannelConfig;
